import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { LodgeApiError } from "../api/client";
import type { OrganisationRecord, RecordCandidate } from "../api/hooks";
import {
  useRecordCandidates,
  useUpdateOrganisationRecord,
} from "../api/hooks";
import { SlideOver } from "../components/SlideOver";

import styles from "./-AccountLinkPanel.module.scss";

// What put a candidate on the list, in the operator's words rather than the
// query's. A percentage nobody can check is worse than a sentence they can:
// "someone here uses this domain" is a fact they can accept or reject.
function evidenceFor(candidate: RecordCandidate): string {
  if (candidate.matchedDomain !== "") {
    return `Someone at this account uses ${candidate.matchedDomain}`;
  }

  return "The names are similar";
}

export function AccountLinkPanel({
  account,
  onClose,
  onLinked,
}: {
  account: { id: string; name: string };
  onClose: () => void;
  onLinked: () => void;
}) {
  const { data: candidates, isPending, isError } = useRecordCandidates(account.id);
  const update = useUpdateOrganisationRecord();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");

  // The whole record goes back, because the write shape is the whole record:
  // sending only the link would clear every other field.
  function link(record: OrganisationRecord) {
    setMessage("");

    update.mutate(
      {
        id: record.id,
        record: {
          name: record.name,
          kind: record.kind,
          county: record.county,
          hectares: record.hectares,
          website: record.website,
          charityNumber: record.charityNumber,
          companyNumber: record.companyNumber,
          organisationId: account.id,
        },
      },
      {
        onSuccess: onLinked,
        onError: (error: unknown) => {
          if (error instanceof LodgeApiError && error.status === 409) {
            // Refetched here rather than relying on the mutation's onSuccess,
            // which does not run on a failure. Telling someone to reopen a
            // panel that would show them the same stale list is worse than
            // saying nothing.
            queryClient.invalidateQueries({ queryKey: ["lodge", "record-candidates"] });
            queryClient.invalidateQueries({ queryKey: ["lodge", "unlinked-accounts"] });

            setMessage(
              "Something else was linked to this account while you were deciding. This list has been refreshed.",
            );
            return;
          }

          setMessage("That could not be linked. Try again.");
        },
      },
    );
  }

  return (
    <SlideOver title={account.name} onClose={onClose}>
      <p className={styles.lead}>
        Which of our records is this customer? Nothing is suggested on its own -
        each of these is here for a reason you can check.
      </p>

      {isPending && (
        <div className={styles.state} role="status">
          Looking for candidates…
        </div>
      )}

      {isError && (
        <div className={styles.state} role="alert">
          The candidates could not be loaded.
        </div>
      )}

      {candidates?.length === 0 && (
        <div className={styles.state} role="status">
          Nothing here looks like this account. Add it as a new organisation from
          the list behind this panel.
        </div>
      )}

      {message !== "" && (
        <div className={styles.error} role="alert">
          {message}
        </div>
      )}

      <ul className={styles.candidates}>
        {candidates?.map((candidate) => (
          <li key={candidate.organisation.id} className={styles.candidate}>
            <div className={styles.candidateName}>{candidate.organisation.name}</div>
            <div className={styles.candidateMeta}>
              {[candidate.organisation.kind, candidate.organisation.county]
                .filter((part) => part !== "")
                .join(" · ")}
            </div>
            <div className={styles.evidence}>{evidenceFor(candidate)}</div>
            <button
              type="button"
              className={styles.link}
              disabled={update.isPending}
              onClick={() => link(candidate.organisation)}
            >
              This is them
            </button>
          </li>
        ))}
      </ul>
    </SlideOver>
  );
}
